import React, { useState, useEffect } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import { useAuth } from '../../auth/AuthProvider';
import { entraEnabled, legacyAuthEnabled } from '../../auth/msalConfig';
import './Login.css';

interface LoginProps {
  onLoginSuccess: () => void;
}

type LoginStep = 'email' | 'password' | 'setup-password';

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [step, setStep] = useState<LoginStep>('email');
  const [displayName, setDisplayName] = useState<string | null>(null);

  // Check if already logged in
  useEffect(() => {
    if (entraEnabled) {
      setCheckingSession(false);
      return;
    }
    const checkSession = async () => {
      try {
        await siftApiClient.me();
        onLoginSuccess();
      } catch {
        // Not logged in, stay on login page
      } finally {
        setCheckingSession(false);
      }
    };
    checkSession();
  }, [onLoginSuccess]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await siftApiClient.checkEmail(email.trim());
      
      if (!result.exists) {
        setError('No account found with this email');
        setLoading(false);
        return;
      }
      
      setDisplayName(result.display_name || null);
      
      if (result.needs_password_setup) {
        setStep('setup-password');
      } else {
        setStep('password');
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to check email');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await siftApiClient.login(email.trim(), password);
      onLoginSuccess();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEntraLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      await auth.login();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Microsoft sign-in failed';
      setError(message);
      setLoading(false);
    }
  };

  const handleSetupPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await siftApiClient.setupPassword(email.trim(), password);
      onLoginSuccess();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to set up password');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setError(null);
  };

  // Show loading while checking session
  if (checkingSession) {
    return (
      <div className="login-container glass-app-background">
        <div className="login-card glass-card">
          <div className="login-header">
            <h1>SIFT</h1>
            <p>Checking session...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container glass-app-background">
      <div className="login-card glass-card glass-glow">
        <div className="login-header">
          <h1>SIFT</h1>
          <p className="glass-text-secondary">Strategic Insights Field Tool</p>
        </div>
        
        {error && <div className="login-error">{error}</div>}

        {entraEnabled && (
          <>
            <p className="glass-text-secondary" style={{ marginBottom: '1rem' }}>
              Sign in with Entra SSO to access SIFT.
            </p>
            <button type="button" disabled={loading} className="login-button" onClick={handleEntraLogin}>
              {loading ? 'Redirecting...' : 'Entra SSO'}
            </button>
            {legacyAuthEnabled && (
              <p className="glass-text-secondary" style={{ margin: '1rem 0 0.5rem' }}>
                Or use your existing email and password.
              </p>
            )}
          </>
        )}

        {!entraEnabled && !legacyAuthEnabled && (
          <div className="login-error">Sign-in is not configured for this environment.</div>
        )}

        {legacyAuthEnabled && step === 'email' && (
          <form onSubmit={handleEmailSubmit}>
            <div className="form-group">
              <label htmlFor="email" className="glass-text-primary">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@coxautoinc.com"
                className="glass-input"
                required
                autoComplete="email"
                autoFocus
              />
            </div>
            
            <button type="submit" disabled={loading} className="login-button">
              {loading ? 'Checking...' : 'Continue'}
            </button>
          </form>
        )}

        {legacyAuthEnabled && step === 'password' && (
          <form onSubmit={handleLoginSubmit}>
            <div className="form-group">
              <div className="email-display">
                <span className="glass-text-secondary">{email}</span>
                <button 
                  type="button" 
                  onClick={handleBackToEmail} 
                  className="change-email-link"
                >
                  Change
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="password" className="glass-text-primary">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="glass-input"
                required
                autoComplete="current-password"
                autoFocus
              />
            </div>
            
            <button type="submit" disabled={loading} className="login-button">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {legacyAuthEnabled && step === 'setup-password' && (
          <form onSubmit={handleSetupPasswordSubmit}>
            <div className="setup-password-header">
              <p className="glass-text-primary">
                Welcome{displayName ? `, ${displayName}` : ''}! 
              </p>
              <p className="glass-text-secondary">
                Please set up your password to continue.
              </p>
            </div>
            
            <div className="form-group">
              <div className="email-display">
                <span className="glass-text-secondary">{email}</span>
                <button 
                  type="button" 
                  onClick={handleBackToEmail} 
                  className="change-email-link"
                >
                  Change
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="new-password" className="glass-text-primary">New Password</label>
              <input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="glass-input"
                required
                minLength={6}
                autoComplete="new-password"
                autoFocus
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirm-password" className="glass-text-primary">Confirm Password</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="glass-input"
                required
                autoComplete="new-password"
              />
            </div>
            
            <button type="submit" disabled={loading} className="login-button">
              {loading ? 'Setting up...' : 'Set Password & Sign In'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
